import { Box } from '@chakra-ui/react';
import { SearchInput, tools } from '@halo/ui-chat-react';
import { StateEnum } from '@halo/chat-sdk-react';
import { useContactsStore } from '@/store/contacts';
import { ItemSelector } from './ItemSelector';

interface ContactsContentProps {
  resetTrigger: number;
  maxSelectedCount: number;
  selectChange: ({ contactIds }: { contactIds: string[] }) => void;
}

export function ContactsContent({
  resetTrigger,
  maxSelectedCount,
  selectChange,
}: ContactsContentProps) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const { contacts: allContacts } = useContactsStore();
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);

  const contacts = useMemo(() => {
    return allContacts.filter((contact) => {
      const isKeyErr = !(contact.have_group_key && contact.have_user_key);
      const isDeleteAccount = contact.state === StateEnum.DELETE_ACCOUNT;
      const isMatch = contact.name.toLowerCase().includes(searchValue.toLowerCase());
      return !isKeyErr && !isDeleteAccount && isMatch;
    });
  }, [allContacts, searchValue]);

  const onSelectContact = useCallback(
    (contactId: string) => {
      setSelectedContactIds((prev) => {
        if (prev.includes(contactId)) {
          return prev.filter((id) => id !== contactId);
        }
        if (prev.length >= maxSelectedCount) {
          tools.toaster.error({
            title: t('tips.maxContactsForwardCount', { count: maxSelectedCount }),
          });
          return prev;
        }
        return [...prev, contactId];
      });
    },
    [maxSelectedCount, t]
  );

  useEffect(() => {
    selectChange({ contactIds: selectedContactIds });
  }, [selectedContactIds, selectChange]);

  useEffect(() => {
    if (resetTrigger) {
      setSelectedContactIds([]);
      setSearchValue('');
    }
  }, [resetTrigger]);

  return (
    <Box className="ContactsContent">
      <SearchInput
        inputProps={{
          bg: 'white',
        }}
        value={searchValue}
        onChange={(value) => setSearchValue(value)}
      />
      <Box
        className="ChatsContentList scrollHover"
        maxH="400px"
        overflowY="auto"
        bg="white"
        borderRadius="lg"
        mt="3"
        px="2"
        py="1.5"
      >
        <Box>
          {contacts.map((contact) => (
            <ItemSelector
              key={contact.user_id}
              id={contact.user_id}
              name={contact.name}
              username={contact.username}
              avatar={contact.avatar}
              isChecked={selectedContactIds.includes(contact.user_id)}
              onClick={onSelectContact}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
